import { AxiosPromise, AxiosResponse } from 'axios';

import { apiService } from '@app/services';
import {
    PswResetServerResponse,
    UserDataPswResetBySms,
    UserDataPswChange,
    SetIsResetToken,
    SetIsError,
    DataForSubmitEmailCode
} from '@app/models';

class AccountService {
    readonly apiBase: string = '/api/account';

    checkIsUserCanResetPassword(
        username: string,
        handleError: Function,
        handleSuccess: Function
    ): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/reset/username?type=reset`;

        return apiService.post(
            apiUrl,
            { username },
            {
                onError: handleError,
                onSuccess: handleSuccess,
                local: true
            }
        );
    }

    validateUsername(
        username: string,
        handleError: Function,
        handleSuccess: Function
    ): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/reset/password/metadata`;

        return apiService.get(
            apiUrl,
            {
                params: { username },
                onError: handleError,
                onSuccess: handleSuccess,
                local: true
            }
        );
    }

    resetPassword(userData: Object, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/reset/password`;

        return apiService.post(
            apiUrl,
            userData,
            {
                onError: handleError,
                onSuccess: handleSuccess,
                local: true
            }
        );
    }

    askServerResetPassword(
        userData: UserDataPswResetBySms,
        handleError: Function,
        handleSuccess: Function
    ): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/reset/password/verification-code`;

        return apiService.post(
            apiUrl,
            { ...userData },
            {
                onError: handleError,
                onSuccess: handleSuccess,
                local: true
            }
        );
    }

    submitEmailVerificationCode(
        userData: DataForSubmitEmailCode,
        handleError: Function,
        handleSuccess: Function
    ): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/reset/sms`;

        return apiService.post(
            apiUrl,
            { ...userData },
            {
                onError: handleError,
                onSuccess: handleSuccess,
                local: true
            }
        );
    }

    changePassword(
        userData: UserDataPswChange,
        handleError: Function,
        handleSuccess: Function
    ): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/reset/password/confirmation`;

        return apiService.post(
            apiUrl,
            { ...userData },
            {
                onError: handleError,
                onSuccess: handleSuccess,
                local: true
            }
        );
    }

    check2FAResetMetadata(
        jsonObject: { contexttoken?: string },
        handleError: Function,
        handleSuccess: Function
    ): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/reset/2fa/availability`;

        return apiService.get(
            apiUrl,
            {
                params: jsonObject,
                onError: handleError,
                onSuccess: handleSuccess,
                local: true
            }
        );
    }

    getDataForResetMfa(
        contextToken: string,
        handleError: Function
    ): AxiosPromise<PswResetServerResponse> {
        const apiUrl: string = `${this.apiBase}/reset/2fa`;


        return apiService
            .post(
                apiUrl,
                { contextToken: contextToken },
                {
                    onError: handleError,
                    local: true
                }
            )
            .then((response: AxiosResponse<PswResetServerResponse>) => {
                return response;
            });
    }

    checkUsersRegisteredPhone(
        userId: string,
        handleError: Function,
        handleSuccess: Function
    ): AxiosPromise<void> {
        const apiUrl: string = `${this.apiBase}/reset/checkUsersRegisteredPhone`;

        return apiService.post(
            apiUrl,
            { userId },
            {
                onError: handleError,
                onSuccess: handleSuccess,
                local: true
            }
        );
    }

    resetMFAToken(
        contextToken: string,
        verificationCode: string,
        setIsResetToken: SetIsResetToken,
        setIsError: SetIsError
    ): AxiosPromise<void> {
        const apiUrl: string = `${this.apiBase}/reset/2fa/confirmation`;

        return apiService.post(
            apiUrl,
            { contextToken, verificationCode },
            {
                onError: () => {
                    setIsError(true);
                },
                onSuccess: () => {
                    setIsResetToken(true);
                },
                local: true
            }
        );
    }
}

export const accountService = new AccountService();
